<template>
    <div>
        <AdminPageHeader
            title="Bandeja de solicitudes 3D"
            description="Solicitudes recibidas desde la web pública: cotizaciones por archivo, pedidos del catálogo y personalizaciones."
        />

        <AdmCard class="ordenes__filters">
            <div class="ordenes__filter-grid">
                <AdmField v-model="filter.requestType" label="Tipo de solicitud" type="select">
                    <option value="">Todos los tipos</option>
                    <option value="print_only">Sólo impresión</option>
                    <option value="modeling_and_print">Modelado + impresión</option>
                    <option value="catalog_order">Pedido del catálogo</option>
                    <option value="custom_order">Personalizado</option>
                </AdmField>

                <AdmField v-model="filter.status" label="Estado" type="select">
                    <option value="">Todos los estados</option>
                    <option v-for="(meta, key) in statuses" :key="key" :value="key">{{ meta.label }}</option>
                </AdmField>

                <AdmField v-model="filter.contactEmail" label="Email del remitente" placeholder="cliente@correo.com" />

                <div class="ordenes__filter-actions">
                    <AdmButton :icon="Search" :loading="loading" @click="applyFilters">Aplicar filtros</AdmButton>
                    <AdmButton variant="ghost" @click="clearFilters">Limpiar</AdmButton>
                </div>
            </div>
        </AdmCard>

        <AdmTable
            class="ordenes__table"
            :columns="columns"
            :rows="items"
            :loading="loading"
            empty-message="No hay solicitudes registradas."
        >
            <template #cell-requestType="{ value }">
                <AdmBadge :tone="typeMeta(value).tone" :label="typeMeta(value).label" />
            </template>

            <template #cell-status="{ value }">
                <AdmBadge :tone="statusMeta(value).tone" :label="statusMeta(value).label" />
            </template>

            <template #cell-contactEmail="{ value }">
                <a :href="`mailto:${value}`" class="ordenes__mail">{{ value }}</a>
            </template>

            <template #cell-attachment="{ row }">
                <AdmButton
                    v-if="row.attachment"
                    variant="ghost"
                    size="sm"
                    :icon="Download"
                    @click="onDownload(row)"
                >
                    {{ truncate(row.attachment.originalName, 25) }}
                </AdmButton>
                <span v-else class="adm-muted">—</span>
            </template>

            <template #cell-actions="{ row }">
                <div class="ordenes__actions">
                    <AdmButton variant="secondary" size="sm" @click="openDetail(row.id)">Ver</AdmButton>
                    <AdmButton variant="danger" size="sm" :icon="Trash2" @click="onQuickDelete(row.id)" />
                </div>
            </template>
        </AdmTable>

        <AdmPagination
            :page="page"
            :total-pages="totalPages"
            :total-items="totalItems"
            @update:page="goToPage"
        />

        <OrdenDetalleModal
            v-if="detailId"
            :order-id="detailId"
            @close="detailId = null"
            @updated="fetchItems"
            @deleted="fetchItems"
        />
    </div>
</template>

<script>
/**
 * Bandeja de solicitudes 3D — migrada en el plan 27_09_2026 (F3/T18).
 *
 * Se retiran: la barra `#menu` duplicada (ahora en el sidebar), la
 * redefinición local de `.button`, la tabla `.data-table` propia y los nueve
 * pares `.badge-*` / `.status-*` con colores de una paleta ajena. La lógica de
 * datos (`getOrders`, `deleteOrder`, `downloadAttachment`) no cambia.
 */
import { Search, Download, Trash2 } from 'lucide-vue-next';
import { getOrders, deleteOrder, downloadAttachment } from '@/services/dbPrint3dAdmin.js';
import OrdenDetalleModal from './OrdenDetalleModal.vue';
import AdminPageHeader from '@/components/Admin/AdminPageHeader.vue';
import AdmCard from '@/components/Admin/ui/AdmCard.vue';
import AdmField from '@/components/Admin/ui/AdmField.vue';
import AdmButton from '@/components/Admin/ui/AdmButton.vue';
import AdmTable from '@/components/Admin/ui/AdmTable.vue';
import AdmPagination from '@/components/Admin/ui/AdmPagination.vue';
import AdmBadge, { ORDER_STATUS, REQUEST_TYPE } from '@/components/Admin/ui/AdmBadge.vue';
import { notify } from '@/composables/useAdmToast.js';

export default {
    name: 'Print3DOrdenesPage',
    components: {
        OrdenDetalleModal, AdminPageHeader, AdmCard, AdmField,
        AdmButton, AdmTable, AdmPagination, AdmBadge,
    },
    data() {
        return {
            items: [],
            loading: false,
            page: 1,
            limit: 15,
            totalPages: 1,
            totalItems: 0,
            filter: { requestType: '', status: '', contactEmail: '', dateFrom: '', dateTo: '' },
            detailId: null,
            statuses: ORDER_STATUS,
            Search, Download, Trash2,
        };
    },
    computed: {
        // En computed y no en data(): una columna usa `formatDate`, y apoyarse
        // en que los métodos ya existan al construir `data()` es frágil.
        columns() {
            return [
                { key: 'id', label: '#', width: '70px' },
                { key: 'requestType', label: 'Tipo' },
                { key: 'status', label: 'Estado' },
                { key: 'contactName', label: 'Remitente' },
                { key: 'contactEmail', label: 'Email' },
                { key: 'attachment', label: 'Adjunto' },
                { key: 'createdAt', label: 'Recibida', format: (v) => this.formatDate(v) },
                { key: 'actions', label: 'Acciones', align: 'right', width: '150px' },
            ];
        },
    },
    methods: {
        async fetchItems() {
            this.loading = true;
            const offset = (this.page - 1) * this.limit;
            const { items, totalPages, totalItems } = await getOrders(offset, this.limit, this.filter);
            this.items = items;
            this.totalPages = totalPages;
            this.totalItems = totalItems;
            this.loading = false;
        },
        applyFilters() {
            this.page = 1;
            this.fetchItems();
        },
        clearFilters() {
            this.filter = { requestType: '', status: '', contactEmail: '', dateFrom: '', dateTo: '' };
            this.applyFilters();
        },
        goToPage(page) {
            this.page = page;
            this.fetchItems();
        },
        openDetail(id) { this.detailId = id; },
        async onDownload(order) {
            const { error } = await downloadAttachment(order.id, order.attachment.originalName);
            if (error) notify.error(`Error al descargar: ${error}`);
        },
        async onQuickDelete(id) {
            if (!confirm(`¿Eliminar la solicitud #${id}? Esta acción no se puede deshacer.`)) return;
            const { error } = await deleteOrder(id);
            if (error) {
                notify.error(`Error al eliminar: ${error}`);
                return;
            }
            notify.success(`Solicitud #${id} eliminada.`);
            this.fetchItems();
        },
        statusMeta(status) {
            return ORDER_STATUS[status] || { label: status || '—', tone: 'neutral' };
        },
        typeMeta(type) {
            return REQUEST_TYPE[type] || { label: type || '—', tone: 'neutral' };
        },
        formatDate(d) {
            if (!d) return '—';
            return new Date(d).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
        },
        truncate(s, n) { return s && s.length > n ? s.slice(0, n - 1) + '…' : s; },
    },
    mounted() { this.fetchItems(); },
};
</script>

<style scoped>
.ordenes__filters { margin-bottom: var(--adm-gap); }

.ordenes__filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--adm-gap);
    align-items: end;
}

.ordenes__filter-actions {
    display: flex;
    gap: var(--adm-gap-sm);
    flex-wrap: wrap;
}

.ordenes__table { margin-bottom: var(--adm-gap-sm); }

.ordenes__mail { color: var(--color-action); }

.ordenes__actions {
    display: flex;
    gap: 0.35rem;
    justify-content: flex-end;
}
</style>
